import { Grid } from "@material-ui/core";
import { Form, Formik } from "formik";
import { toPairs } from "lodash";
import { observer } from "mobx-react";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { t } from "../../i18n/util";
import { applicationStore } from "../../stores/ApplicationStore";
import { THEME } from "../../theme/ThemeStore";
import { ApplicationValues } from "../../types/models";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { AddField } from "../ui/AddField";
import { LinkField } from "../ui/LinkField";
import { FormButtons, HrBotErrorMessage } from "../ui/Primitives";
import { UploadField } from "../ui/UploadField";
import { fileErrorToString, megabytes } from "../util/helpers";
import { ADDITIONAL_FILES_MAX_SIZE_MB } from "../../config";

export type DataUploadFileErrors = {
    resume: (FileError | null)[];
    photo: (FileError | null)[];
    portfolio: (FileError | null)[];
    video: (FileError | null)[];
    motivationalLetter: (FileError | null)[];
};

type DataUploadFileErrorType = keyof DataUploadFileErrors;

type Props = {
    onSubmit: (model: ApplicationValues) => void;
    onBack: () => void;
};

export type FileError = "type" | "size" | "amount" | "required" | null;

export const DataUploadForm = observer(({ onSubmit, onBack }: Props) => {
    const isMobile = useMobileMediaQuery();
    const isDal = THEME.THEME_NAME === "dal";

    const [fileErrors, setFileErrors] = React.useState<DataUploadFileErrors>({
        resume: [],
        photo: [],
        portfolio: [],
        video: [],
        motivationalLetter: []
    });

    const errors = (allFileErrors: DataUploadFileErrors) =>
        Object.keys(allFileErrors).some((fieldKey) =>
            allFileErrors[fieldKey as DataUploadFileErrorType].some(
                (fieldFileError: FileError) => fieldFileError === "type" || fieldFileError === "size"
            )
        );

    return (
        <>
            <div style={{ marginBottom: isMobile ? 23 : 36 }}>
                <h3 style={{ marginBottom: isDal ? 12 : undefined }}>{t("screen.dataUpload.title")}</h3>
                {isDal && <p className="applicationCaption">{t("screen.dataUpload.caption")}</p>}
            </div>
            <Formik
                initialValues={applicationStore.currentApplication}
                onSubmit={(model: ApplicationValues) => {
                    if (!model.resume) {
                        if (!fileErrors.resume.includes("required")) {
                            setFileErrors({
                                ...fileErrors,
                                resume: [...fileErrors.resume, "required"]
                            });
                        }
                    } else if (!errors(fileErrors)) {
                        onSubmit(model);
                    }
                }}
                validateOnChange
                validateOnBlur
            >
                {({ values, setFieldValue, dirty, isValid, setTouched }) => {
                    React.useEffect(() => {
                        applicationStore.currentApplication = values;
                        if (!values.resume || errors(fileErrors)) {
                            applicationStore.canNavigate = false;
                            applicationStore.validateForm = () => {
                                if (!fileErrors.resume.includes("required")) {
                                    setFileErrors({ ...fileErrors, resume: [...fileErrors.resume, "required"] });
                                }
                            };
                        } else {
                            applicationStore.canNavigate = true;
                            const tmpIndex = fileErrors.resume.indexOf("required");
                            if (tmpIndex > -1) {
                                setFileErrors({
                                    ...fileErrors,
                                    resume: [
                                        ...fileErrors.resume.slice(0, tmpIndex),
                                        ...fileErrors.resume.slice(tmpIndex + 1)
                                    ]
                                });
                            }
                        }

                        if (values.resume && !errors(fileErrors)) {
                            applicationStore.isStepFinished["dataUpload"] = true;
                        }
                    }, [dirty, isValid, setTouched, values]);

                    return (
                        <Form style={{ height: "100%", flexGrow: 1 }}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                    justifyContent: isMobile ? "flex-start" : "space-between"
                                }}
                            >
                                <div>
                                    <Grid container spacing={2}>
                                        <UploadField
                                            name={t("screen.dataUpload.resume")}
                                            type="application/pdf"
                                            value={values.resume}
                                            tooltip={t("screen.dataUpload.csv.instructions")}
                                            onUploadFile={(file) => {
                                                setFieldValue("resume", file);
                                            }}
                                            onDelete={() => {
                                                setFieldValue("resume", null);
                                            }}
                                            onSetFileErrors={(newFileErrors) => {
                                                setFileErrors({
                                                    ...fileErrors,
                                                    resume: newFileErrors
                                                });
                                            }}
                                        />

                                        {THEME.THEME_NAME !== "dal" && (
                                            <UploadField
                                                name={t("screen.dataUpload.video")}
                                                type="video/mp4"
                                                value={values.video}
                                                tooltip={t("screen.dataUpload.video.instructions")}
                                                optional
                                                fileSize={megabytes(95)}
                                                onUploadFile={(file) => {
                                                    setFieldValue("video", file);
                                                }}
                                                onDelete={() => {
                                                    setFieldValue("video", null);
                                                }}
                                                onSetFileErrors={(newFileErrors) => {
                                                    setFileErrors({
                                                        ...fileErrors,
                                                        video: newFileErrors
                                                    });
                                                }}
                                            />
                                        )}

                                        <UploadField
                                            name={t("screen.dataUpload.portfolio")}
                                            type="application/pdf"
                                            value={values.portfolio}
                                            optional
                                            onUploadFile={(file) => {
                                                setFieldValue("portfolio", file);
                                            }}
                                            onDelete={() => {
                                                setFieldValue("portfolio", null);
                                            }}
                                            onSetFileErrors={(newFileErrors) => {
                                                setFileErrors({
                                                    ...fileErrors,
                                                    portfolio: newFileErrors
                                                });
                                            }}
                                        />

                                        <UploadField
                                            name={t("screen.dataUpload.photo")}
                                            type="image/jpeg"
                                            value={values.photo}
                                            optional
                                            onUploadFile={(file) => {
                                                setFieldValue("photo", file);
                                            }}
                                            onDelete={() => {
                                                setFieldValue("photo", null);
                                            }}
                                            onSetFileErrors={(newFileErrors) => {
                                                setFileErrors({
                                                    ...fileErrors,
                                                    photo: newFileErrors
                                                });
                                            }}
                                        />

                                        {THEME.THEME_NAME !== "dal" && (
                                            <UploadField
                                                name={t("screen.dataUpload.motivationalLetter")}
                                                type="application/pdf"
                                                value={values.motivationalLetter}
                                                optional
                                                onUploadFile={(file) => {
                                                    setFieldValue("motivationalLetter", file);
                                                }}
                                                onDelete={() => {
                                                    setFieldValue("motivationalLetter", null);
                                                }}
                                                onSetFileErrors={(newFileErrors) => {
                                                    setFileErrors({
                                                        ...fileErrors,
                                                        motivationalLetter: newFileErrors
                                                    });
                                                }}
                                            />
                                        )}

                                        {values.optionalFiles.map((value, index) => (
                                            <UploadField
                                                key={index}
                                                name={`optionalFiles[${index}]`}
                                                type="application/pdf"
                                                value={value}
                                                onDelete={() => {
                                                    setFieldValue(
                                                        "optionalFiles",
                                                        values.optionalFiles.filter(
                                                            (fileValue, fileIndex) => fileIndex !== index
                                                        )
                                                    );
                                                }}
                                                onUploadFile={(file) => {
                                                    setFieldValue(`optionalFiles[${index}]`, file);
                                                }}
                                            />
                                        ))}

                                        {values.optionalLinks.map((value, index) => (
                                            <LinkField
                                                value={value}
                                                key={index}
                                                onDelete={() => {
                                                    setFieldValue(
                                                        "optionalLinks",
                                                        values.optionalLinks.filter(
                                                            (linkValue, linkIndex) => linkIndex !== index
                                                        )
                                                    );
                                                }}
                                            />
                                        ))}

                                        <AddField
                                            // Calculate the remaining size by subtracting the size of all the files
                                            remainingSize={
                                                megabytes(ADDITIONAL_FILES_MAX_SIZE_MB) -
                                                values.optionalFiles.map((file) => file.size).reduce((a, b) => a + b, 0)
                                            }
                                            addFile={(file) => {
                                                setFieldValue("optionalFiles", [...values.optionalFiles, file]);
                                            }}
                                            addLink={(link: string) => {
                                                setFieldValue("optionalLinks", [...values.optionalLinks, link]);
                                            }}
                                        />
                                    </Grid>
                                    <div style={{ marginTop: 22 }}>
                                        {toPairs(fileErrors).map((fieldFileErrors) =>
                                            fieldFileErrors[1].map(
                                                (fieldFileError) =>
                                                    fieldFileError && (
                                                        <HrBotErrorMessage
                                                            key={uuidv4()}
                                                            label={fileErrorToString(
                                                                fieldFileErrors[0],
                                                                fieldFileError
                                                            )}
                                                        />
                                                    )
                                            )
                                        )}
                                    </div>
                                </div>
                                <FormButtons
                                    labelBack={t("screen.details.button.back")}
                                    labelSubmit={t("screen.dataUpload.button.toMessage")}
                                    onBack={onBack}
                                    middleButtons
                                    style={{ marginTop: 18 }}
                                />
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
});
